import "bootstrap/dist/css/bootstrap.min.css";
import { FormikProps, withFormik } from "formik";
import React, { useState } from "react";
import { Button, Col, Form, InputGroup } from "react-bootstrap";
import { FaPlus, FaTimes } from "react-icons/fa";
import * as yup from "yup";
import { ISignatureState, locations, organizations } from "./Models";

interface IFormProps {
    initialValues: ISignatureState;
    onValueChange: (values: ISignatureState) => void;
    onSubmition: (values: ISignatureState, isValid: boolean) => void;
}

const phoneRegExp =
    /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/;
///^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const schema = yup.object({
    location: yup.string().min(1).required(),
    fullName: yup.string().min(2).max(100).required(),
    email: yup
        .string()
        .email("Must be a valid email address")
        .max(100)
        .required(),
    pronouns: yup.string().notRequired(),
    org: yup.string().min(1).required(),
    jobTitle: yup.string().min(2).max(100).required(),
    workPhone: yup.string().required(),
    cellPhone: yup.string().notRequired(),
});

const InnerForm = (props: IFormProps & FormikProps<ISignatureState>) => {
    const { touched, errors, values, handleSubmit, handleChange, handleBlur } =
        props;

    const [showCellNumber, setShowCellNumber] = useState(false);

    const handleValueChange = (value: Partial<ISignatureState>) => {
        const results = Object.assign(values, value);
        props.onValueChange(results);
    };

    // get the most recent value change, don't wait for event to trigger.
    const getUpdatedValue = (
        el: HTMLInputElement
    ): Partial<ISignatureState> => {
        switch (el.name) {
            case "fullName":
                return { fullName: el.value };
            case "email":
                isEmailModified = true;
                return { email: el.value };
            case "pronouns":
                return { pronouns: el.value };
            case "org":
                return { org: el.value };
            case "jobTitle":
                return { jobTitle: el.value };
            case "workPhone":
                return { workPhone: el.value };
            case "cellPhone":
                return { cellPhone: el.value };
            case "location":
                if (el.value) {
                    setDefaultPhoneNumber(el.value);
                }
                return { location: el.value };
            default:
                return {};
        }
    };

    let isEmailModified: boolean = false;

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        console.log(e.target.value);
        handleChange(e);
        const el = e.target as HTMLInputElement;

        handleValueChange(getUpdatedValue(el));
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const el = e.target as HTMLInputElement;

        if (el.name === "email" && !props.touched.email) {
            el.select();
        }
    };

    const setDefaultPhoneNumber = (value: string) => {
        if (value) {
            const location = locations[parseInt(value)];

            // has phone number
            if (location.telephone) {
                props.setFieldValue("workPhone", location.telephone, true);
                handleValueChange({ workPhone: location.telephone });
            } else {
                if (
                    !props.touched.workPhone &&
                    props.values.workPhone &&
                    props.values.workPhone.length > 0
                ) {
                    props.setFieldValue("workPhone", "", true);
                    handleValueChange({ workPhone: "" });
                }
            }
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        handleBlur(e);
        const el = e.target as HTMLInputElement;

        // guess email
        if (el.name === "fullName") {
            if (
                props.values.fullName &&
                props.values.fullName.length > 0 &&
                !isEmailModified &&
                !props.touched.email
            ) {
                const email = `${props.values.fullName
                    .trim()
                    .split(" ")
                    .join(".")
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .toLocaleLowerCase()}@mindfully.com`;

                //props.getFieldHelpers("email").setTouched(ture);
                props.setFieldValue("email", email, true);
                handleValueChange({ email: email });
            }
        }
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        handleSubmit();

        props.onSubmition(values, props.isValid);
    };

    const getCellVisibility = (): string => {
        if (showCellNumber) {
            return "block";
        }

        return "none";
    };

    const toggleCellVisibility = () => {
        if (showCellNumber) {
            setShowCellNumber(false);

            props.setFieldValue("cellPhone", "");
            handleValueChange({ cellPhone: "" });
        } else {
            setShowCellNumber(true);
            // handleValueChange({ cellPhone: values.cellPhone });
        }
    };

    return (
        <div style={{ backgroundColor: "#eaeaea", padding: 20 }}>
            <Form
                enableReinitialize={true}
                onSubmit={onSubmit}
                style={{ width: "90%" }}
            >
                <Form.Row>
                    <Col>
                        <h6 style={{ marginBottom: 20 }}>Signature Options</h6>

                        <Form.Group controlId="fullName">
                            <Form.Control
                                type="text"
                                name="fullName"
                                size="sm"
                                placeholder="Enter First & Last Name"
                                value={values.fullName}
                                onChange={onChange}
                                onBlur={onBlur}
                                defaultValue=""
                                isInvalid={
                                    touched.fullName && !!errors.fullName
                                }
                            ></Form.Control>
                            <Form.Control.Feedback type="invalid">
                                {errors.fullName}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="title">
                            <Form.Control
                                type="text"
                                name="jobTitle"
                                placeholder="Enter Job Title"
                                size="sm"
                                value={values.jobTitle}
                                onChange={onChange}
                                onBlur={onBlur}
                                isInvalid={
                                    touched.jobTitle && !!errors.jobTitle
                                }
                            ></Form.Control>
                            <Form.Control.Feedback type="invalid">
                                {errors.jobTitle}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="pronouns">
                            <Form.Control
                                as="select"
                                name="pronouns"
                                size="sm"
                                value={values.pronouns}
                                onChange={onChange}
                                onBlur={onBlur}
                                isInvalid={
                                    touched.pronouns && !!errors.pronouns
                                }
                            >
                                <option value="" disabled selected>
                                    -- Pronouns (Optional)
                                </option>
                                <option value="0">None</option>
                                <option value="She/Her/Hers">
                                    She/Her/Hers
                                </option>
                                <option value="He/Him/His">He/Him/His</option>
                                <option value="They/Them/Theirs">
                                    They/Them/Theirs
                                </option>
                                {/* <option value="Fae/Faer/Faers">
                                    Fae/Faer/Faers
                                </option>
                                <option value="Ze/Zir/Zirs">Ze/Zir/Zirs</option>
                                <option value="It/It/Its">It/It/Its</option> */}
                                {/* <option value="Ask me for my pronouns">
                                    Ask me for my pronouns
                                </option> */}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">
                                {errors.pronouns}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="org">
                            <Form.Control
                                as="select"
                                name="org"
                                size="sm"
                                value={values.org}
                                onChange={onChange}
                                onBlur={onBlur}
                                isInvalid={touched.org && !!errors.org}
                            >
                                <option value="" disabled selected>
                                    -- Select Department
                                </option>
                                {organizations.map((organization, index) => (
                                    <option
                                        value={index}
                                        key={`organization-${index}`}
                                    >
                                        {organization.name}
                                    </option>
                                ))}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">
                                {errors.org}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="location">
                            <Form.Control
                                as="select"
                                size="sm"
                                name="location"
                                value={values.location}
                                onChange={onChange}
                                onBlur={onBlur}
                                isInvalid={
                                    touched.location && !!errors.location
                                }
                            >
                                <option value="" disabled selected>
                                    -- Select Location
                                </option>
                                {locations.map((location, index) => (
                                    <option
                                        value={index}
                                        key={`location-${index}`}
                                    >
                                        {location.name}
                                    </option>
                                ))}
                            </Form.Control>
                            <Form.Control.Feedback type="invalid">
                                {errors.location}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="email">
                            <Form.Control
                                type="email"
                                name="email"
                                size="sm"
                                placeholder="Enter Email"
                                value={values.email}
                                onChange={onChange}
                                onFocus={onFocus}
                                onBlur={onBlur}
                                isInvalid={touched.org && !!errors.email}
                            ></Form.Control>
                            <Form.Control.Feedback type="invalid">
                                {errors.email}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="workPhone">
                            <InputGroup>
                                <Form.Control
                                    type="tel"
                                    name="workPhone"
                                    size="sm"
                                    placeholder="Enter Work Phone"
                                    value={values.workPhone}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    isInvalid={
                                        touched.workPhone && !!errors.workPhone
                                    }
                                ></Form.Control>
                                <Form.Control.Feedback type="invalid">
                                    {errors.workPhone}
                                </Form.Control.Feedback>
                                <InputGroup.Append>
                                    <Button
                                        style={{
                                            display: showCellNumber
                                                ? "none"
                                                : "block",
                                        }}
                                        title="Add Cell (OPTiONAL)"
                                        onClick={toggleCellVisibility}
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        <FaPlus size={16} />
                                    </Button>
                                </InputGroup.Append>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group
                            controlId="cellPhone"
                            style={{ display: getCellVisibility() }}
                        >
                            <InputGroup>
                                <Form.Control
                                    type="tel"
                                    name="cellPhone"
                                    size="sm"
                                    placeholder="Enter Cell Phone"
                                    value={values.cellPhone}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    isInvalid={!!errors.cellPhone}
                                ></Form.Control>
                                <Form.Control.Feedback type="invalid">
                                    {errors.cellPhone}
                                </Form.Control.Feedback>
                                <InputGroup.Append>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={toggleCellVisibility}
                                    >
                                        <FaTimes size={16} />
                                    </Button>
                                </InputGroup.Append>
                            </InputGroup>
                        </Form.Group>
                    </Col>
                </Form.Row>
                <Form.Row>
                    <Button
                        style={{ width: "100%" }}
                        type="submit"
                        variant="primary"
                        disabled={!(props.isValid && props.dirty)}
                    >
                        Create
                    </Button>
                </Form.Row>
            </Form>
        </div>
    );
};

const SignatureForm = withFormik<IFormProps, ISignatureState>({
    validationSchema: schema,
    handleSubmit: (values, actions) => {
        actions.setSubmitting(false);
    },
})(InnerForm);

export { SignatureForm };
